import {
  GiftOption,
  GiftSectionFormValue,
} from "@/checkout/Sections/SubscriptionSection/types";
import { getCountNoun } from "@/utils/formatCountLabel";

export type GiftSlot = {
  option?: GiftOption;
};

export type GiftSelectionStatus = {
  /** One group per section, one slot per item in the package. */
  slotGroups: GiftSlot[][];
  isComplete: boolean;
  /** Empty when nothing in the package is up to the customer. */
  text: string;
};

/** A section is a choice when it offers more items than the customer gets. */
export const isChoiceSection = ({
  count,
  options,
}: GiftSectionFormValue): boolean => count > 0 && options.length > count;

export const getGiftNoun = (options: GiftOption[], count: number): string =>
  options.length > 0 && options.every((option) => option.isBook)
    ? getCountNoun(count, "bok", "bøker")
    : getCountNoun(count, "gave", "gaver");

const joinWithOg = (parts: string[]): string =>
  parts.length <= 1
    ? (parts[0] ?? "")
    : `${parts.slice(0, -1).join(", ")} og ${parts[parts.length - 1]}`;

export const getGiftSelectionStatus = (
  sections: GiftSectionFormValue[],
  /** SKUs in the order they were picked, so earlier picks keep their slot. */
  pickOrder: string[] = [],
): GiftSelectionStatus => {
  const choiceSections = sections.filter(isChoiceSection);

  const slotGroups = sections.map(({ count, options }) => {
    const selected = options
      .filter((option) => option.selected)
      .sort((a, b) => pickOrder.indexOf(a.sku) - pickOrder.indexOf(b.sku));
    return Array.from({ length: count }, (_, index) => ({
      option: selected[index],
    }));
  });

  const incomplete = choiceSections
    .map(({ count, options }) => {
      const selectedCount = options.filter((option) => option.selected).length;
      return { options, selectedCount, remaining: count - selectedCount };
    })
    .filter(({ remaining }) => remaining > 0);

  const missing = incomplete.map(
    ({ options, remaining }) =>
      `${remaining} ${getGiftNoun(options, remaining)}`,
  );
  // "til" (more) once at the end, as soon as the customer has started on what is missing.
  const more = incomplete.some(({ selectedCount }) => selectedCount > 0)
    ? " til"
    : "";

  const isComplete = incomplete.length === 0;

  return {
    slotGroups,
    isComplete,
    text:
      choiceSections.length === 0
        ? ""
        : isComplete
          ? "Pakken er klar"
          : `Velg ${joinWithOg(missing)}${more}`,
  };
};
